<script setup lang="ts">
import { computed } from "vue";

import { formatNumber } from "@/services/Utils/DecimalFormatter";
import Skeleton from "@/components/Reusables/Loading/Skeleton.vue";

const props = withDefaults(
  defineProps<{
    // Left side of the row above the bar. Callers own every word of it: this used to append
    // "MAPPED" itself, which made the bar unusable for anything but mapping coverage.
    label: (value: string) => string;
    // Right side of the row above the bar, right-aligned away from the label.
    subLabel?: (value: string) => string;
    value?: number;
    thresholds?: { value: number; color: string }[];
    loading?: boolean;
    // While loading, the label row is replaced by a Skeleton, so a bar cannot print a value it does
    // not have yet. Pass this only when the label degrades to a static name for the whole time
    // `loading` is true. A label that still carries a figure must keep the Skeleton, otherwise the
    // old number reads as current while its replacement is being fetched.
    showLabelWhileLoading?: boolean;
    ariaLabel?: string;
  }>(),
  {
    value: 0,
    subLabel: () => "",
    showLabelWhileLoading: false,
    thresholds: () => [
      { value: 0, color: "bg-slate-300" },
      { value: 25, color: "bg-app-red-300" },
      { value: 75, color: "bg-app-yellow-400" },
      { value: 100, color: "bg-app-green-400" },
    ],
    loading: false,
    ariaLabel: "Progress",
  }
);

// Single fixed bar height — previously the bar expanded/collapsed (24px ↔ 10px)
// based on scroll position, but that was scrapped along with the click-to-toggle
// behavior. The bar is now always rendered at the expanded size.
const BAR_HEIGHT = 10;

// Each threshold's colour applies to values BELOW its value, and the last entry's colour applies
// from its value upwards. So the default below reads: under 25 red, under 75 yellow, 75 and up
// green, with the first entry only used at 0. Worth knowing when passing a custom array, because
// getting it backwards is silent and paints every bar the same colour.
const valueColor = computed(() => {
  const first = props.thresholds[0];
  const last = props.thresholds[props.thresholds.length - 1];
  const restOfThresholds = props.thresholds.slice(1, -1);

  if (props.value >= last.value) {
    return last.color;
  }

  if (props.value <= first.value) {
    return first.color;
  }

  if (props.value < restOfThresholds[0]?.value) {
    return restOfThresholds[0].color;
  }

  for (let i = 0; i < restOfThresholds.length; i++) {
    if (props.value < restOfThresholds[i + 1]?.value) {
      return restOfThresholds[i + 1].color;
    }
  }

  return last.color;
});

const colorOpacityMap: Record<string, { track: string; fill: string }> = {
  "bg-slate-300": { track: "bg-slate-300/20", fill: "bg-slate-300/65" },
  "bg-app-red-300": {
    track: "bg-app-red-300/20 dark:bg-app-red-500/20",
    fill: "bg-app-red-300/65 dark:bg-app-red-500/80",
  },
  "bg-app-yellow-400": {
    track: "bg-app-yellow-400/20 dark:bg-app-yellow-500/20",
    fill: "bg-app-yellow-400/65 dark:bg-app-yellow-500/80",
  },
  "bg-app-green-400": {
    track: "bg-app-green-400/20 dark:bg-app-green-500/20",
    fill: "bg-app-green-400/65 dark:bg-app-green-500/80",
  },
};

const trackClass = computed(() => {
  if (props.loading) return "bg-slate-300/20";
  return colorOpacityMap[valueColor.value]?.track ?? "bg-slate-300/20";
});

const fillClass = computed(() => {
  return colorOpacityMap[valueColor.value]?.fill ?? "bg-slate-300/65";
});
</script>

<template>
  <div class="flex flex-col">
    <div
      class="mb-px-4 t-footnote text-type-default flex w-full items-center justify-between gap-3 whitespace-nowrap select-none">
      <!-- Loading: skeleton reserves the label-row height so the widget doesn't
           jump when the real value lands in the fixed-height header slot. Skipped for callers whose
           label is a name rather than a figure while loading, so the bar can still say what it is. -->
      <Skeleton
        v-if="loading && !showLabelWhileLoading"
        width="w-10"
        height="h-2.5"
        rounded="full"
        decorative />
      <template v-else>
        <span class="truncate">{{ label(formatNumber(value)) }}</span>
        <span v-if="subLabel(formatNumber(value))">{{ subLabel(formatNumber(value)) }}</span>
      </template>
    </div>

    <div
      role="progressbar"
      :aria-valuenow="loading ? undefined : value"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-label="ariaLabel"
      :aria-busy="loading"
      class="duration-normal ease-standard relative flex w-full overflow-hidden text-slate-600 transition-all"
      :class="trackClass">
      <!--
      Width kept inline because the percentage is data-driven. Held at 0 while loading, so the
      track claims no value until there is one, then grows into place on the width transition.
    -->
      <div
        class="duration-normal ease-standard whitespace-nowrap transition-all"
        :class="fillClass"
        :style="`width: ${loading ? 0 : value}%; height: ${BAR_HEIGHT}px`" />

      <!--
      Loading layer: the same slate pulse the label row's Skeleton uses just above it. This used to
      be a saturated red-to-green gradient sliding across the full track, which was loud on its own
      and much worse in the project toolbar, where up to three of these bars sit side by side and
      every navigation inside a project restarted all of them. Absolute, so it cannot shrink along
      with the fill's width transition when loading ends.
    -->
      <div
        v-if="loading"
        class="pointer-events-none absolute inset-0 animate-pulse bg-slate-200"
        aria-hidden="true" />
    </div>
  </div>
</template>
